/**
 * The entities one invoice line billed, looked up on demand.
 *
 * An aggregated line — one annual fee covering several hundred radios — shows
 * only how many it bills and of which kind. This is where billing answers
 * "which ones": the billed-entity grid, scoped to that line, searchable by the
 * ICCID or site name captured when the invoice was generated, so a device a
 * customer asks about can be found on the invoice that billed it.
 */

import { Box } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import { EntityGrid } from '@sophocles/data-grids';

const RESOURCE_PATH = 'billed-entities';

/** Tall enough to page through a line's entities without the dialog growing. */
const GRID_HEIGHT = '60vh';

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {number} props.invoiceId - the invoice being read; the grid requires it
 * @param {number|null} [props.invoiceItemId] - one line of it, or every line
 * @param {string} [props.lineName] - the line's name, for the dialog title
 */
function BilledEntitiesDialog({ open, onClose, invoiceId, invoiceItemId = null, lineName }) {
  const { t } = useTranslation();

  const extraParams = useMemo(() => {
    const params = { invoice_id: invoiceId };
    if (invoiceItemId != null) params.invoice_item_id = invoiceItemId;
    return params;
  }, [invoiceId, invoiceItemId]);

  const options = useMemo(
    () => ({
      showSearch: true,
      searchPlaceholder: t('billing.searchBilledEntities', 'Search by ICCID or site'),
      countNoun: t('billing.billedEntitiesCountNoun', 'billed entities'),
      infiniteScroll: true,
      persistKey: 'billingBilledEntitiesList',
      gridProps: {
        density: 'compact',
        disableRowSelectionOnClick: true,
        sx: { flex: 1, minHeight: 0 },
      },
    }),
    [t],
  );

  const title = lineName
    ? `${t('billing.billedEntities', 'Billed Entities')} — ${lineName}`
    : t('billing.billedEntities', 'Billed Entities');

  return (
    <ResponsiveDialog title={title} open={open} onClose={onClose} maxWidth="lg" hideCancel>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: GRID_HEIGHT, minHeight: 0, width: '100%' }}>
        <EntityGrid basePath={RESOURCE_PATH} extraParams={extraParams} options={options} />
      </Box>
    </ResponsiveDialog>
  );
}

export default BilledEntitiesDialog;
